/**
 * Amanah — shared docx theme & branding helpers.
 *
 * Every downloadable Word document in the Life Planning Kit imports from
 * this module so headers, footers, cover titles, colours and typography
 * stay identical across all files.
 *
 * Design decisions (Feb 2026):
 *  • Serif = Georgia (web-safe fallback for our Cormorant Garamond brand
 *    face — Word / Google Docs both render Georgia consistently).
 *  • Sans  = Segoe UI (Windows default, cross-platform legible).
 *  • Colour palette matches /app/design_guidelines.json (Emerald / Gold / Stone).
 *  • Header  = "Amanah by Zakat.International" wordmark left, small tag right.
 *  • Footer  = URL centred with running page number on the right.
 *  • Cover   = deep emerald title, gold underline, "AMANAH LIFE PLANNING KIT"
 *              kicker, generation timestamp, and a subtle Islamic geometric
 *              divider (unicode ✦ · ✦).
 *
 * All helpers accept the dynamically-imported `docx` module object so
 * callers pay the bundle cost only when a download is triggered.
 */

// Brand colours (hex without leading #)
export const AMANAH_COLORS = {
  emerald: '0D2D22', // deep emerald 950 — headings, primary accents
  emeraldMid: '116343',
  gold: 'C9A44B',    // primary gold — dividers, kicker text
  goldSoft: 'E5D5A8',
  stone: '5C5C5C',   // body text
  stoneSoft: '9E9E9E',
  ink: '1A1A1A',
};

export const AMANAH_FONTS = {
  serif: 'Georgia',
  sans: 'Segoe UI',
  mono: 'Consolas',
};

// -----------------------------------------------------------------
// Header — Amanah wordmark + right-side context tag
// -----------------------------------------------------------------
export const buildAmanahHeader = (docx, { docTitle = '', contextTag = '' } = {}) => {
  const {
    Header, Paragraph, TextRun, Table, TableRow, TableCell,
    WidthType, BorderStyle, AlignmentType,
  } = docx;

  const noBorder = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
  const bottomGold = { style: BorderStyle.SINGLE, size: 6, color: AMANAH_COLORS.gold };

  const cellNoBorder = (children, { w = 50, align = AlignmentType.LEFT } = {}) => new TableCell({
    width: { size: w, type: WidthType.PERCENTAGE },
    borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder },
    children: [new Paragraph({ alignment: align, children })],
  });

  return new Header({
    children: [
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: noBorder, left: noBorder, right: noBorder,
          bottom: bottomGold,
          insideHorizontal: noBorder, insideVertical: noBorder,
        },
        rows: [
          new TableRow({
            children: [
              cellNoBorder([
                new TextRun({
                  text: 'AMANAH',
                  bold: true, size: 20, color: AMANAH_COLORS.emerald,
                  font: AMANAH_FONTS.serif,
                }),
                new TextRun({
                  text: '  by Zakat.International',
                  size: 16, color: AMANAH_COLORS.stone,
                  font: AMANAH_FONTS.sans,
                }),
              ]),
              cellNoBorder([
                new TextRun({
                  text: contextTag || docTitle,
                  size: 16, color: AMANAH_COLORS.emeraldMid, italics: true,
                  font: AMANAH_FONTS.sans,
                }),
              ], { align: AlignmentType.RIGHT }),
            ],
          }),
        ],
      }),
      // Tight spacer under the gold divider
      new Paragraph({ children: [new TextRun({ text: '', size: 10 })] }),
    ],
  });
};

// -----------------------------------------------------------------
// Footer — url + page number
// -----------------------------------------------------------------
export const buildAmanahFooter = (docx) => {
  const {
    Footer, Paragraph, TextRun, Table, TableRow, TableCell,
    WidthType, BorderStyle, AlignmentType, PageNumber,
  } = docx;

  const noBorder = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
  const topGold = { style: BorderStyle.SINGLE, size: 4, color: AMANAH_COLORS.gold };
  const cellNoBorder = (children, { w = 33, align = AlignmentType.LEFT } = {}) => new TableCell({
    width: { size: w, type: WidthType.PERCENTAGE },
    borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder },
    children: [new Paragraph({ alignment: align, children })],
  });

  return new Footer({
    children: [
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: topGold, left: noBorder, right: noBorder, bottom: noBorder,
          insideHorizontal: noBorder, insideVertical: noBorder,
        },
        rows: [
          new TableRow({
            children: [
              cellNoBorder([
                new TextRun({
                  text: 'zakat.international',
                  size: 16, color: AMANAH_COLORS.emeraldMid,
                  font: AMANAH_FONTS.sans,
                }),
              ]),
              cellNoBorder([
                new TextRun({
                  text: 'Ethical Wealth · Shared Responsibility',
                  size: 14, color: AMANAH_COLORS.stoneSoft, italics: true,
                  font: AMANAH_FONTS.sans,
                }),
              ], { align: AlignmentType.CENTER }),
              cellNoBorder([
                new TextRun({ text: 'p. ', size: 14, color: AMANAH_COLORS.stone, font: AMANAH_FONTS.sans }),
                new TextRun({ children: [PageNumber.CURRENT], size: 14, color: AMANAH_COLORS.stone, font: AMANAH_FONTS.sans }),
              ], { align: AlignmentType.RIGHT }),
            ],
          }),
        ],
      }),
    ],
  });
};

// -----------------------------------------------------------------
// Cover title paragraphs — inserted at the top of the doc body.
// Returns an array of paragraphs to spread into `children`.
// -----------------------------------------------------------------
export const buildAmanahCover = (docx, {
  kicker = 'AMANAH LIFE PLANNING KIT',
  title,
  subtitle = '',
  generatedAt = new Date().toISOString().slice(0, 10),
} = {}) => {
  const { Paragraph, TextRun, AlignmentType } = docx;
  const paragraphs = [
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 240, after: 60 },
      children: [
        new TextRun({
          text: kicker,
          bold: true, size: 16, color: AMANAH_COLORS.gold,
          font: AMANAH_FONTS.sans,
          characterSpacing: 60,
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 80 },
      children: [
        new TextRun({
          text: title,
          bold: true, size: 44, color: AMANAH_COLORS.emerald,
          font: AMANAH_FONTS.serif,
        }),
      ],
    }),
    // Ornament divider
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 120 },
      children: [
        new TextRun({
          text: '✦ · ✦',
          size: 20, color: AMANAH_COLORS.gold,
        }),
      ],
    }),
  ];
  if (subtitle) {
    paragraphs.push(new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 60 },
      children: [
        new TextRun({
          text: subtitle,
          italics: true, size: 22, color: AMANAH_COLORS.stone,
          font: AMANAH_FONTS.serif,
        }),
      ],
    }));
  }
  paragraphs.push(new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { after: 300 },
    children: [
      new TextRun({
        text: `Generated ${generatedAt}  ·  zakat.international`,
        size: 15, color: AMANAH_COLORS.stoneSoft,
        font: AMANAH_FONTS.sans,
      }),
    ],
  }));
  return paragraphs;
};

// -----------------------------------------------------------------
// Themed section wrapper — returns the `sections` array entry with
// header + footer + margins pre-configured. Callers pass the body
// `children` array.
// -----------------------------------------------------------------
export const buildAmanahSection = (docx, { children, docTitle, contextTag }) => ({
  properties: {
    page: {
      margin: {
        top:    1000,  // ~0.7" — leaves room for the branded header
        right:  1000,
        bottom: 900,   // room for footer
        left:   1000,
      },
    },
  },
  headers: { default: buildAmanahHeader(docx, { docTitle, contextTag }) },
  footers: { default: buildAmanahFooter(docx) },
  children,
});

// -----------------------------------------------------------------
// Themed heading paragraph — use in place of the old h(text) helper
// so every section-header inside every doc looks the same.
// -----------------------------------------------------------------
export const themedHeading = (docx, text, level = 2) => {
  const { Paragraph, TextRun, HeadingLevel, AlignmentType } = docx;
  const sizes = { 1: 32, 2: 26, 3: 22 };
  return new Paragraph({
    alignment: AlignmentType.LEFT,
    spacing: { before: 220, after: 100 },
    heading: level === 1 ? HeadingLevel.HEADING_1 : (level === 2 ? HeadingLevel.HEADING_2 : HeadingLevel.HEADING_3),
    children: [
      new TextRun({
        text,
        bold: true,
        size: sizes[level] || 24,
        color: AMANAH_COLORS.emerald,
        font: AMANAH_FONTS.serif,
      }),
    ],
  });
};

// Body paragraph with the theme font applied — replaces raw new Paragraph
// creations where callers want body copy.
export const themedBody = (docx, text, opts = {}) => {
  const { Paragraph, TextRun } = docx;
  return new Paragraph({
    spacing: { after: 100, line: 300 },
    ...opts.paragraph,
    children: [
      new TextRun({
        text,
        size: 20,
        color: AMANAH_COLORS.ink,
        font: AMANAH_FONTS.sans,
        ...opts.run,
      }),
    ],
  });
};

// Bulleted list item — reusable across kit documents.
export const themedBullet = (docx, text) => {
  const { Paragraph, TextRun } = docx;
  return new Paragraph({
    bullet: { level: 0 },
    spacing: { after: 60 },
    children: [
      new TextRun({
        text,
        size: 20,
        color: AMANAH_COLORS.ink,
        font: AMANAH_FONTS.sans,
      }),
    ],
  });
};

// -----------------------------------------------------------------
// Themed table — emerald header row, gold borders, alternating stripes.
//
//   themedTable(docx, {
//     columns: [{ label: 'Source', width: 60 }, { label: 'Amount', width: 40 }],
//     rows: [['Salary', '3,500'], ['Rent', '1,200']],
//     stripeEven: true,               // alternate row shading (default true)
//     tone: 'emerald' | 'gold',       // header colour
//   })
// -----------------------------------------------------------------
export const themedTable = (docx, {
  columns,
  rows = [],
  stripeEven = true,
  tone = 'emerald',
}) => {
  const {
    Paragraph, TextRun, Table, TableRow, TableCell, WidthType, BorderStyle, AlignmentType, ShadingType,
  } = docx;

  const borderColor = AMANAH_COLORS.gold;
  const outer = { style: BorderStyle.SINGLE, size: 6, color: borderColor };
  const inner = { style: BorderStyle.SINGLE, size: 2, color: 'DCC98E' };
  const borders = {
    top: outer, bottom: outer, left: outer, right: outer,
    insideHorizontal: inner, insideVertical: inner,
  };

  const headerBg = tone === 'gold' ? AMANAH_COLORS.gold : AMANAH_COLORS.emerald;
  const headerFg = tone === 'gold' ? AMANAH_COLORS.emerald : 'FFFFFF';
  const stripeBg = 'FAF6EA'; // very soft cream — matches gold-50

  const headerCell = (label, w) => new TableCell({
    width: { size: w, type: WidthType.PERCENTAGE },
    shading: { type: ShadingType.CLEAR, color: 'auto', fill: headerBg },
    margins: { top: 100, bottom: 100, left: 120, right: 120 },
    children: [new Paragraph({
      alignment: AlignmentType.LEFT,
      children: [new TextRun({
        text: label, bold: true, size: 20, color: headerFg, font: AMANAH_FONTS.sans,
      })],
    })],
  });

  const bodyCell = (text, w, striped) => new TableCell({
    width: { size: w, type: WidthType.PERCENTAGE },
    shading: striped ? { type: ShadingType.CLEAR, color: 'auto', fill: stripeBg } : undefined,
    margins: { top: 80, bottom: 80, left: 120, right: 120 },
    children: [new Paragraph({
      children: [new TextRun({
        text: String(text ?? ''),
        size: 20,
        color: AMANAH_COLORS.ink,
        font: AMANAH_FONTS.sans,
      })],
    })],
  });

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders,
    rows: [
      new TableRow({
        tableHeader: true,
        children: columns.map((c) => headerCell(c.label, c.width)),
      }),
      ...rows.map((cells, ri) => new TableRow({
        children: cells.map((val, ci) => bodyCell(val, columns[ci]?.width || 100 / cells.length, stripeEven && ri % 2 === 1)),
      })),
    ],
  });
};

// -----------------------------------------------------------------
// Themed callout — soft-tinted box with kicker + body text.
// Great for "Why this matters" / "Islamic principle" side-notes.
// -----------------------------------------------------------------
export const themedCallout = (docx, { kicker, body, tone = 'emerald' }) => {
  const {
    Paragraph, TextRun, Table, TableRow, TableCell, WidthType, BorderStyle, ShadingType,
  } = docx;
  const fills = {
    emerald: { bg: 'EAF3EE', border: AMANAH_COLORS.emeraldMid, kicker: AMANAH_COLORS.emerald },
    gold:    { bg: 'FAF3DF', border: AMANAH_COLORS.gold,       kicker: '8A6A18' },
    stone:   { bg: 'F4F0EA', border: '9E8C6E',                 kicker: '4E4633' },
  };
  const t = fills[tone] || fills.emerald;
  const outer = { style: BorderStyle.SINGLE, size: 6, color: t.border };
  const noBorder = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: outer, bottom: outer, left: outer, right: outer,
      insideHorizontal: noBorder, insideVertical: noBorder,
    },
    rows: [new TableRow({
      children: [new TableCell({
        width: { size: 100, type: WidthType.PERCENTAGE },
        shading: { type: ShadingType.CLEAR, color: 'auto', fill: t.bg },
        margins: { top: 160, bottom: 160, left: 200, right: 200 },
        children: [
          new Paragraph({
            spacing: { after: 60 },
            children: [new TextRun({
              text: (kicker || '').toUpperCase(),
              bold: true, size: 16, color: t.kicker,
              font: AMANAH_FONTS.sans,
              characterSpacing: 40,
            })],
          }),
          new Paragraph({
            spacing: { line: 300 },
            children: [new TextRun({
              text: body, size: 20, color: AMANAH_COLORS.ink,
              font: AMANAH_FONTS.sans,
            })],
          }),
        ],
      })],
    })],
  });
};

// -----------------------------------------------------------------
// Themed quote — Arabic + translation + attribution.
// Use for Qur'an ayat and Hadith blockquotes at the start / end of docs.
// -----------------------------------------------------------------
export const themedQuote = (docx, { arabic, translation, source }) => {
  const {
    Paragraph, TextRun, Table, TableRow, TableCell, WidthType, BorderStyle, ShadingType, AlignmentType,
  } = docx;
  const leftBar = { style: BorderStyle.SINGLE, size: 24, color: AMANAH_COLORS.gold };
  const noBorder = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: noBorder, bottom: noBorder, right: noBorder,
      left: leftBar,
      insideHorizontal: noBorder, insideVertical: noBorder,
    },
    rows: [new TableRow({
      children: [new TableCell({
        width: { size: 100, type: WidthType.PERCENTAGE },
        shading: { type: ShadingType.CLEAR, color: 'auto', fill: 'FBF8F0' },
        margins: { top: 180, bottom: 180, left: 240, right: 200 },
        children: [
          arabic ? new Paragraph({
            alignment: AlignmentType.RIGHT,
            spacing: { after: 100 },
            bidirectional: true,
            children: [new TextRun({
              text: arabic, size: 28, color: AMANAH_COLORS.emerald,
              font: 'Amiri', rightToLeft: true,
            })],
          }) : null,
          translation ? new Paragraph({
            spacing: { after: 80, line: 320 },
            children: [new TextRun({
              text: `\u201C${translation}\u201D`,
              italics: true, size: 22, color: AMANAH_COLORS.ink,
              font: AMANAH_FONTS.serif,
            })],
          }) : null,
          source ? new Paragraph({
            children: [new TextRun({
              text: `— ${source}`,
              size: 16, color: AMANAH_COLORS.stone,
              font: AMANAH_FONTS.sans,
            })],
          }) : null,
        ].filter(Boolean),
      })],
    })],
  });
};

// -----------------------------------------------------------------
// Signature block — Donor + Witnesses grid with hand-write lines.
// -----------------------------------------------------------------
export const themedSignatureBlock = (docx, {
  parties = [{ label: 'Signature' }, { label: 'Date' }],
} = {}) => {
  const { Paragraph, TextRun, UnderlineType } = docx;
  const HW = (w = 50) => new TextRun({
    text: '\u00A0'.repeat(w),
    underline: { type: UnderlineType.SINGLE, color: '888888' },
    size: 22,
  });
  return parties.map((p, i) => new Paragraph({
    spacing: { before: i === 0 ? 320 : 160 },
    children: [
      new TextRun({
        text: `${p.label}: `,
        bold: true, size: 22, color: AMANAH_COLORS.emerald,
        font: AMANAH_FONTS.sans,
      }),
      HW(p.width || 50),
    ],
  }));
};

// Ornament divider — matches the cover ornament.
export const themedOrnament = (docx) => {
  const { Paragraph, TextRun, AlignmentType } = docx;
  return new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { before: 240, after: 240 },
    children: [new TextRun({
      text: '✦ · ✦', size: 20, color: AMANAH_COLORS.gold,
    })],
  });
};
